import React from 'react';
import { LucideIcon } from 'lucide-react';

import { cn } from '../lib/utils';

interface EmptyStateProps {
  icon?: LucideIcon;
  /** Bold one-liner: what would be here. */
  title: string;
  /**
   * The title's element. 'p' (default) inside a page; 'h1' only when the empty
   * state IS the page (NotFound, AccessDenied) — a focusable 16/600 h1, so
   * route-change focus lands on it. Never 'h1' under a PageHeader, which
   * already renders one.
   */
  titleAs?: 'p' | 'h1' | 'h2';
  /** Teaching line: how to get it. */
  description?: string;
  /** Gated CTA ("Create sensor") or a reset. */
  action?: React.ReactNode;
  className?: string;
}

/**
 * The standard empty treatment: dashed container, title, teaching line, CTA.
 * Purely presentational.
 *
 * A LIST's empty state goes through `ListEmptyState`, which chooses the mode
 * in the order search → filter → first-run and writes the no-results copy
 * itself; never hand-write "No X match" here. Use EmptyState directly only for
 * a non-list surface (no organization selected, a whole-page first run).
 * One-line empties inside dialogs, rows and side panels stay muted text.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  titleAs = 'p',
  description,
  action,
  className,
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center',
      className,
    )}
  >
    {Icon ? <Icon aria-hidden="true" className="mb-3 h-6 w-6 text-muted-foreground" /> : null}
    {titleAs === 'h1' ? (
      <h1 tabIndex={-1} className="text-base font-semibold outline-none">{title}</h1>
    ) : titleAs === 'h2' ? (
      <h2 className="text-base font-semibold">{title}</h2>
    ) : (
      <p className="text-sm font-semibold">{title}</p>
    )}
    {description ? (
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
    ) : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);
